import { FilterDropdown, illustrations } from '@dawn/ui';
import type { ReactNode } from 'react';

import type { DashboardFilterConfig } from '@/config/dashboard.types';
import { useDashboardFilterStore } from '@/stores/dashboard-filter';

export interface GlobalFilterRowProps {
  filters: DashboardFilterConfig[];
}

/**
 * Resolve a filter config's `icon` string identifier to a rendered icon node.
 * Extend this map as new icon identifiers are introduced.
 */
function resolveFilterIcon(iconId: string | undefined): ReactNode {
  if (iconId === 'calendar') {
    return <img src={illustrations.calendar} alt="" className="h-4 w-4" />;
  }
  return undefined;
}

export function GlobalFilterRow({ filters }: GlobalFilterRowProps) {
  const filterValues = useDashboardFilterStore((s) => s.filters);

  const leftFilters = filters
    .filter((f) => f.position === 'left')
    .sort((a, b) => a.order - b.order);

  const rightFilters = filters
    .filter((f) => f.position === 'right')
    .sort((a, b) => a.order - b.order);

  function handleChange(filterId: string, value: string) {
    useDashboardFilterStore.getState().setFilter(filterId, value);
  }

  function renderFilter(filter: DashboardFilterConfig, align: 'start' | 'end') {
    return (
      <FilterDropdown
        key={filter.id}
        selectionMode="radio"
        options={filter.options}
        value={filterValues[filter.id] ?? filter.defaultValue ?? ''}
        onChange={(value) =>
          handleChange(filter.id, typeof value === 'string' ? value : (value[0] ?? ''))
        }
        triggerLabel={filter.label}
        triggerIcon={resolveFilterIcon(filter.icon)}
        triggerTestId={`filter-${filter.id}-trigger`}
        align={align}
      />
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2" data-testid="filters-left">
        {leftFilters.map((filter) => renderFilter(filter, 'start'))}
      </div>
      <div className="flex items-center gap-2" data-testid="filters-right">
        {rightFilters.map((filter) => renderFilter(filter, 'end'))}
      </div>
    </div>
  );
}
